/**
 * The empty state of a list, drawn inside the table's frame (hybrid-3c §9):
 * a large watermark icon, a 600-weight title and one quiet line saying what to
 * change. `ResponsiveDatagrid` uses it by default; the `<List>` around it takes
 * `empty={false}` so react-admin never swaps the whole list for its own
 * "no records yet" page.
 */

import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, Typography, useTheme } from '@mui/material';
import { InboxOutlined as EmptyIcon } from '@mui/icons-material';

export interface DatagridEmptyProps {
  /** Headline; defaults to the generic "no results". */
  title?: string;
  /** What to adjust; defaults to the generic search/filters hint. */
  hint?: string;
  icon?: React.ReactNode;
}

export const DatagridEmpty: React.FC<DatagridEmptyProps> = ({ title, hint, icon }) => {
  const theme = useTheme();
  const translate = useTranslate();

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1,
        py: 9,
        px: 2,
        backgroundColor: theme.palette.background.paper,
        '& > .MuiSvgIcon-root': { fontSize: 112, color: theme.palette.grey[100] },
      }}
    >
      {icon ?? <EmptyIcon />}
      <Typography sx={{ fontWeight: 600 }}>{title ?? translate('shell.empty.title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {hint ?? translate('shell.empty.hint')}
      </Typography>
    </Box>
  );
};

export default DatagridEmpty;
